import { Link, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from './AuthContext';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

export const AdminBlogList = () => {
  const { admin, authFetch, logout } = useAuth();
  const { lang } = useParams();
  const activeLang = lang || 'en';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/blog');
      if (!res.ok) throw new Error('Failed to load');
      setPosts(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (admin) load(); /* eslint-disable-next-line */ }, [admin]);

  if (admin === undefined) return <div className="min-h-screen bg-page" />;
  if (admin === null) return <Navigate to={`/${activeLang}/admin/login`} replace />;

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    const res = await authFetch(`/blog/${slug}`, { method: 'DELETE' });
    if (res.ok) load();
    else alert('Delete failed');
  };

  return (
    <main className="min-h-screen bg-page px-5 md:px-8 py-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ ADMIN</span>
            <span className="font-mono uppercase text-xs tracking-widest2 text-ink">Editor · {admin.email}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to={`/${activeLang}`} className="font-mono uppercase text-xs tracking-widest text-ink link-underline">View site ↗</Link>
            <button type="button" onClick={logout} className="font-mono uppercase text-xs tracking-widest text-ink link-underline" data-testid="admin-logout">
              Sign out ↗
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
          <h1 className="display text-5xl md:text-7xl leading-[0.9] text-ink">
            All posts.
          </h1>
          <Link
            to={`/${activeLang}/admin/blog/new`}
            className="inline-flex items-center gap-2 bg-lime text-ink px-6 py-3.5 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-ink hover:text-lime transition-colors duration-300"
            data-testid="admin-new-post"
          >
            New post <span aria-hidden>+</span>
          </Link>
        </div>

        {error && <p className="font-mono uppercase text-xs text-clay mb-6">{error}</p>}

        {loading ? (
          <p className="serif-italic text-ink/70">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="box-line p-10 md:p-14 text-center">
            <p className="serif-italic text-lg text-ink/70 mb-6">No posts yet.</p>
            <Link to={`/${activeLang}/admin/blog/new`} className="font-mono uppercase text-xs tracking-widest text-ink link-underline">Publish your first post ↗</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="box-line p-4 md:p-5 flex flex-wrap items-center gap-4 justify-between"
                data-testid={`admin-post-${p.slug}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span
                      className={`font-mono uppercase text-[9px] tracking-widest2 px-2 py-0.5 border border-ink ${p.status === 'published' ? 'bg-lime text-ink' : 'bg-yellow text-ink'}`}
                      data-testid={`admin-status-${p.slug}`}
                    >
                      {p.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <span className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60">{formatDate(p.date)}</span>
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="font-mono uppercase text-[10px] tracking-widest text-ink/60">· {t}</span>
                    ))}
                  </div>
                  <p className="font-mono uppercase text-sm md:text-base text-ink truncate">{p.title}</p>
                  <p className="font-mono text-[11px] text-ink/50 mt-1 truncate">/blog/{p.slug}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/${activeLang}/admin/blog/${p.slug}/edit`}
                    className="font-mono uppercase text-[11px] tracking-widest text-ink border border-ink px-3 py-2 hover:bg-lime transition-colors"
                    data-testid={`admin-edit-${p.slug}`}
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(p.slug)}
                    className="font-mono uppercase text-[11px] tracking-widest text-ink border border-ink px-3 py-2 hover:bg-clay hover:text-page transition-colors"
                    data-testid={`admin-delete-${p.slug}`}
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};
